import { CONTACT_EMAIL, GAME_NAME } from '@geosport/shared';
import { Shell } from '../components/Shell';
import { AnalyticsChoice } from './SettingsSheet';

export function Privacy() {
  return (
    <Shell title="Privacy">
      <div className="flex flex-col gap-4 text-sm leading-relaxed text-white/80">
        <p>
          {GAME_NAME} is designed to work without an account and without collecting personal
          information. This page explains what we store and why.
        </p>
        <section>
          <h2 className="mb-1 font-semibold text-white">The player cookie</h2>
          <p>
            When you first play, we create a random player ID and store it in a cookie named{' '}
            <code>player_id</code> for one year, with a copy in your browser’s local storage. It is
            strictly necessary to remember your streak, stats and leaderboard entry. It contains no
            personal data and is not used for advertising.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Display name</h2>
          <p>
            You can set an optional display name that appears on the daily leaderboard. Please do
            not use your real name if you would rather stay anonymous.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Daily reminder</h2>
          <p>
            If you turn on the daily reminder, your browser gives us a push address (run by Google,
            Apple, Mozilla or Microsoft, depending on your browser). We store it with your player
            ID, the time you chose and your device’s time-zone offset, and use it only to send one
            notification a day when you haven’t played yet. The notification carries no data. Turn
            it off in Settings and the address is deleted.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Weekly email</h2>
          <p>
            If you sign up for the weekly email, we store your email address with your player ID and
            send one confirmation email. Only after you confirm do you get one email each Monday
            with your week’s scores, your groups’ tables and any special rounds; nothing else, and
            your address is never shared or sold. The email is sent by our email provider
            (Cloudflare or Resend). Every email has a one-click unsubscribe link, which deletes your
            address, as does turning it off in Settings or deleting your data.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Question reports</h2>
          <p>
            If you report a problem with a question, we store the reason, any note you write and
            your player ID, so that one person counts once. Reports are read only to fix questions.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Analytics</h2>
          <p>
            We use Google Analytics 4 to understand how the game is used: which pages are visited,
            how many rounds are finished and shared, and roughly which countries visitors come from.
            It sets first-party cookies (<code>_ga</code> and <code>_ga_…</code>) that last up to 13
            months. Google Analytics 4 does not log or store IP addresses, and its advertising
            features and Google signals are switched off.
          </p>
          <p className="mt-2">
            In the UK, the EEA and Switzerland it only runs if you allow it. Elsewhere it runs
            unless you switch it off here or in Settings, or your browser sends a Global Privacy
            Control signal.
          </p>
          <div className="mt-3">
            <AnalyticsChoice />
          </div>
          <p>
            We also use Cloudflare Web Analytics to count visits and measure page speed. It sets no
            cookies, stores nothing on your device and builds no profile of you, so it runs for
            everyone. We may also use PostHog in cookieless mode. IP addresses are not stored by us.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Error reporting</h2>
          <p>
            If something breaks, a technical error report (browser type, page, stack trace) may be
            sent to Sentry so we can fix it. Reports do not include your display name.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Your rights</h2>
          <p>
            Under UK GDPR you can ask us to delete the data linked to your player ID: use “Delete my
            data” in Settings, or email{' '}
            <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>{' '}
            with any other privacy question. Clearing your browser cookies and site data also
            removes the link between you and that data.
          </p>
        </section>
        <section>
          <h2 className="mb-1 font-semibold text-white">Map data</h2>
          <p>
            Map tiles are built from OpenStreetMap data via Protomaps and served from our own
            storage. No third-party map provider sees your requests.
          </p>
        </section>
      </div>
    </Shell>
  );
}
